// ─────────────────────────────────────────────────────────────────────────────
// Azure Blob Storage Service - Medical Record Cloud Storage
// Comprehensive blob storage service using Azure Storage SDK
// ─────────────────────────────────────────────────────────────────────────────

import { BlobServiceClient, ContainerClient, BlockBlobClient } from '@azure/storage-blob';
import networkService from '../hooks/services/networkService';

export interface BlobUploadResult {
  success: boolean;
  url?: string;
  error?: string;
  blobName?: string;
}

export interface BlobDownloadResult {
  success: boolean;
  data?: Blob;
  error?: string;
}

export interface BlobStorageConfig {
  enabled: boolean;
  containers: {
    pdfs: {
      name: string;
      sasUrl?: string;
    };
    data: {
      name: string;
      sasUrl?: string;
    };
    images: {
      name: string;
      sasUrl?: string;
    };
  };
}

export class BlobStorageService {
  private config: BlobStorageConfig;
  private containerClients: Map<string, ContainerClient> = new Map();

  constructor(config: BlobStorageConfig) {
    this.config = config;
    
    if (!config.enabled) {
      throw new Error('Blob storage not enabled');
    }

    // Initialize container clients for each container with their respective SAS tokens
    this.initializeContainerClients();
  }

  // Initialize container clients with their respective SAS tokens
  private initializeContainerClients(): void {
    const containers = [
      { type: 'pdfs', config: this.config.containers.pdfs },
      { type: 'data', config: this.config.containers.data },
      { type: 'images', config: this.config.containers.images }
    ];

    for (const container of containers) {
      if (container.config.sasUrl) {
        try {
          const blobServiceClient = new BlobServiceClient(container.config.sasUrl);
          const containerClient = blobServiceClient.getContainerClient(container.config.name);
          this.containerClients.set(container.type, containerClient);
        } catch (error) {
          console.error(`❌ Failed to initialize ${container.type} container client:`, error);
        }
      } else {
        console.warn(`⚠️ No SAS URL provided for ${container.type} container`);
      }
    }
  }

  // Get container client for specific container type
  private getContainerClient(containerType: 'pdfs' | 'data' | 'images'): ContainerClient {
    const containerClient = this.containerClients.get(containerType);
    if (!containerClient) {
      throw new Error(`Container client not available for ${containerType}. Check SAS URL configuration.`);
    }
    return containerClient;
  }

  // Check if device is online before attempting blob operations
  private async checkOnlineStatus(): Promise<{ online: boolean; error?: string }> {
    if (!networkService.isOnline()) {
      return { 
        online: false, 
        error: 'Device is offline - blob storage operations require internet connectivity' 
      };
    }
    return { online: true };
  }

  // Check if container is available and device is online
  private async checkContainerAvailability(containerType: 'pdfs' | 'data' | 'images'): Promise<{ available: boolean; error?: string }> {
    const onlineCheck = await this.checkOnlineStatus();
    if (!onlineCheck.online) {
      return { available: false, error: onlineCheck.error };
    }

    if (!this.isContainerAvailable(containerType)) {
      return { 
        available: false, 
        error: `${containerType} container not configured - missing SAS URL for ${this.config.containers[containerType].name}` 
      };
    }

    return { available: true };
  }

  // Upload PDF reports to pdfs container
  async uploadPdfReport(
    patientId: string, 
    sedationId: string, 
    pdfBlob: Blob, 
    filename?: string
  ): Promise<BlobUploadResult> {
    // Check container availability and online status
    const availabilityCheck = await this.checkContainerAvailability('pdfs');
    if (!availabilityCheck.available) {
      console.warn('⚠️ Cannot upload PDF - container not available:', availabilityCheck.error);
      return {
        success: false,
        error: availabilityCheck.error
      };
    }

    try {
      const blobName = filename || this.generatePdfBlobName(patientId, sedationId);
      const containerClient = this.getContainerClient('pdfs');
      const blockBlobClient = containerClient.getBlockBlobClient(blobName);

      await blockBlobClient.uploadData(pdfBlob, {
        blobHTTPHeaders: {
          blobContentType: 'application/pdf',
          blobContentDisposition: `inline; filename="${filename || blobName}"`
        },
        metadata: {
          patientId,
          sedationId,
          uploadDate: new Date().toISOString(),
          contentType: 'pdf-report',
          fileType: 'SedationReport'
        }
      });

      const url = blockBlobClient.url;

      return {
        success: true,
        url,
        blobName
      };

    } catch (error) {
      console.error('❌ Failed to upload PDF to blob storage:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown upload error'
      };
    }
  }

  // Upload sedation data backup to data container
  async uploadSedationDataBackup(
    patientId: string,
    sedationId: string,
    sedationData: any
  ): Promise<BlobUploadResult> {
    // Check container availability and online status
    const availabilityCheck = await this.checkContainerAvailability('data');
    if (!availabilityCheck.available) {
      console.warn('⚠️ Cannot upload sedation data backup - container not available:', availabilityCheck.error);
      return {
        success: false,
        error: availabilityCheck.error
      };
    }

    try {
      const blobName = this.generateDataBlobName(patientId, sedationId);
      const containerClient = this.getContainerClient('data');
      const blockBlobClient = containerClient.getBlockBlobClient(blobName);

      // Convert data to JSON string
      const jsonData = JSON.stringify(sedationData, null, 2);
      const dataBlob = new Blob([jsonData], { type: 'application/json' });

      await blockBlobClient.uploadData(dataBlob, {
        blobHTTPHeaders: {
          blobContentType: 'application/json',
          blobContentDisposition: `attachment; filename="${blobName}"`
        },
        metadata: {
          patientId,
          sedationId,
          uploadDate: new Date().toISOString(),
          contentType: 'sedation-data-backup',
          dataType: 'SedationRecord'
        }
      });

      const url = blockBlobClient.url;

      return {
        success: true,
        url,
        blobName
      };

    } catch (error) {
      console.error('❌ Failed to upload sedation data backup to blob storage:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown upload error'
      };
    }
  }

  // Upload captured images from intra-sedation to images container
  async uploadSedationImage(
    patientId: string,
    sedationId: string,
    imageBlob: Blob,
    hourNumber: number,
    imageIndex: number = 0
  ): Promise<BlobUploadResult> {
    // Check container availability and online status
    const availabilityCheck = await this.checkContainerAvailability('images');
    if (!availabilityCheck.available) {
      console.warn('⚠️ Cannot upload sedation image - container not available:', availabilityCheck.error);
      return {
        success: false,
        error: availabilityCheck.error
      };
    }

    try {
      const blobName = this.generateImageBlobName(patientId, sedationId, hourNumber, imageIndex);
      const containerClient = this.getContainerClient('images');
      const blockBlobClient = containerClient.getBlockBlobClient(blobName);

      await blockBlobClient.uploadData(imageBlob, {
        blobHTTPHeaders: {
          blobContentType: imageBlob.type || 'image/png',
          blobCacheControl: 'public, max-age=31536000' // 1 year cache
        },
        metadata: {
          patientId,
          sedationId,
          hourNumber: hourNumber.toString(),
          imageIndex: imageIndex.toString(),
          uploadDate: new Date().toISOString(),
          contentType: 'sedation-image'
        }
      });

      const url = blockBlobClient.url;
      return {
        success: true,
        url,
        blobName
      };

    } catch (error) {
      console.error('❌ Failed to upload sedation image to blob storage:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown upload error'
      };
    }
  }

  // Upload multiple images in batch
  async uploadSedationImagesBatch(
    patientId: string,
    sedationId: string,
    images: { blob: Blob; hourNumber: number; index: number }[]
  ): Promise<{ results: BlobUploadResult[]; successCount: number; failureCount: number }> {
    const results: BlobUploadResult[] = [];
    let successCount = 0;
    let failureCount = 0;

    for (let i = 0; i < images.length; i++) {
      const { blob, hourNumber, index } = images[i];
      const result = await this.uploadSedationImage(patientId, sedationId, blob, hourNumber, index);
      results.push(result);
      
      if (result.success) {
        successCount++;
      } else {
        failureCount++;
      }
    }

    return { results, successCount, failureCount };
  }

  // Download blob data
  async downloadBlob(blobName: string, containerType: 'pdfs' | 'data' | 'images'): Promise<BlobDownloadResult> {
    try {
      const containerClient = this.getContainerClient(containerType);
      const blockBlobClient = containerClient.getBlockBlobClient(blobName);


      const downloadResponse = await blockBlobClient.download();
      const downloadedBlob = await this.streamToBlob(downloadResponse.readableStreamBody!);

      return {
        success: true,
        data: downloadedBlob
      };

    } catch (error) {
      console.error('❌ Failed to download blob from storage:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown download error'
      };
    }
  }

  // List blobs for a specific patient/sedation
  async listSedationBlobs(
    patientId: string, 
    sedationId?: string, 
    containerType: 'pdfs' | 'data' | 'images' = 'pdfs'
  ): Promise<string[]> {
    try {
      const containerClient = this.getContainerClient(containerType);
      const prefix = sedationId 
        ? `patients/${patientId}/sedations/${sedationId}/`
        : `patients/${patientId}/`;

      const blobNames: string[] = [];
      
      for await (const blob of containerClient.listBlobsFlat({ prefix })) {
        blobNames.push(blob.name);
      }

      return blobNames;

    } catch (error) {
      console.error('❌ Failed to list blobs:', error);
      return [];
    }
  }

  // Delete blob
  async deleteBlob(blobName: string, containerType: 'pdfs' | 'data' | 'images'): Promise<boolean> {
    try {
      const containerClient = this.getContainerClient(containerType);
      const blockBlobClient = containerClient.getBlockBlobClient(blobName);

      await blockBlobClient.delete();
      return true;

    } catch (error) {
      console.error('❌ Failed to delete blob:', error);
      return false;
    }
  }

  // Check if service is available
  isAvailable(): boolean {
    return this.config.enabled && this.containerClients.size > 0;
  }

  // Check if specific container is available
  isContainerAvailable(containerType: 'pdfs' | 'data' | 'images'): boolean {
    return this.containerClients.has(containerType);
  }

  // Helper method to convert stream to blob
  private async streamToBlob(readableStream: NodeJS.ReadableStream): Promise<Blob> {
    const chunks: Uint8Array[] = [];
    
    return new Promise((resolve, reject) => {
      readableStream.on('data', (chunk) => {
        chunks.push(chunk);
      });
      
      readableStream.on('end', () => {
        const buffer = Buffer.concat(chunks);
        const blob = new Blob([buffer]);
        resolve(blob);
      });
      
      readableStream.on('error', reject);
    });
  }

  // Generate standardized blob names
  private generatePdfBlobName(patientId: string, sedationId: string): string {
    const timestamp = new Date().toISOString().split('T')[0];
    return `patients/${patientId}/sedations/${sedationId}/reports/sedation-report-${timestamp}.pdf`;
  }

  private generateDataBlobName(patientId: string, sedationId: string): string {
    const timestamp = new Date().toISOString().split('T')[0];
    return `patients/${patientId}/sedations/${sedationId}/data/sedation-data-backup-${timestamp}.json`;
  }

  private generateImageBlobName(
    patientId: string, 
    sedationId: string, 
    hourNumber: number, 
    imageIndex: number
  ): string {
    const timestamp = new Date().toISOString().split('T')[0];
    return `patients/${patientId}/sedations/${sedationId}/images/hour-${hourNumber}-image-${imageIndex}-${timestamp}.png`;
  }
}

// Configuration and factory functions
export const getBlobStorageConfig = (): BlobStorageConfig => {
  const hasPdfsSas = !!(process.env.REACT_APP_AZURE_STORAGE_PDFS_SAS_URL);
  const hasDataSas = !!(process.env.REACT_APP_AZURE_STORAGE_DATA_SAS_URL);
  const hasImagesSas = !!(process.env.REACT_APP_AZURE_STORAGE_IMAGES_SAS_URL);
  
  return {
    enabled: hasPdfsSas || hasDataSas || hasImagesSas, // Enable if at least one container is configured
    containers: {
      pdfs: {
        name: process.env.REACT_APP_AZURE_STORAGE_PDFS_CONTAINER || 'pdfs',
        sasUrl: process.env.REACT_APP_AZURE_STORAGE_PDFS_SAS_URL
      },
      data: {
        name: process.env.REACT_APP_AZURE_STORAGE_DATA_CONTAINER || 'sr-offline-data',
        sasUrl: process.env.REACT_APP_AZURE_STORAGE_DATA_SAS_URL
      },
      images: {
        name: process.env.REACT_APP_AZURE_STORAGE_IMAGES_CONTAINER || 'images',
        sasUrl: process.env.REACT_APP_AZURE_STORAGE_IMAGES_SAS_URL
      }
    }
  };
};

export const getBlobStorageService = (): BlobStorageService | null => {
  const config = getBlobStorageConfig();
  
  if (!config.enabled) {
    console.warn('⚠️ Blob storage not configured - using local storage only');
    return null;
  }

  try {
    return new BlobStorageService(config);
  } catch (error) {
    console.error('❌ Failed to initialize blob storage service:', error);
    return null;
  }
};
